import Vue from 'vue';
import type { Module } from 'vuex';
import AgentService from '@/api/agent/AgentService';
import wsGateway from '@/utils/websocketGateway';
import dates from '@/utils/dates';
import { hasPermission } from '@/common/permission';
import type { AgentState, AgentQueueItem, PendingAssistantMessage } from '@/types/agent';

/** 新会话首次发送、尚未返回 conversationId 时的流式占位键 */
export const PENDING_NEW_STREAM_KEY = '__pending_new__';

export function getConversationKey(conversationId?: string | number | null): string {
  return conversationId ? String(conversationId) : PENDING_NEW_STREAM_KEY;
}

export type { AgentState, AgentQueueItem, PendingAssistantMessage };

function cloneMessage(message: any): any {
  if (!message || typeof message !== 'object') return message;
  return {
    ...message,
    attachments: Array.isArray(message.attachments)
      ? message.attachments.map((item: any) => ({ ...item }))
      : message.attachments,
    toolCalls: Array.isArray(message.toolCalls)
      ? message.toolCalls.map((item: any) => ({ ...item }))
      : message.toolCalls,
    reasoningSegments: message.reasoningSegments
      ? { ...message.reasoningSegments }
      : message.reasoningSegments,
    metadata: message.metadata && typeof message.metadata === 'object'
      ? {
          ...message.metadata,
          suggestions: Array.isArray(message.metadata.suggestions)
            ? message.metadata.suggestions.map((item: any) => ({ ...item }))
            : message.metadata.suggestions
        }
      : message.metadata
  };
}

function cloneMessages(messages: any[]): any[] {
  return Array.isArray(messages) ? messages.map(cloneMessage) : [];
}

interface UpdateMessagePayload {
  index: number;
  content?: string;
  toolCalls?: any[];
  reasoning?: string;
  reasoningSegments?: Record<string, string>;
  metadata?: any;
}

function updateMessageList(messages: any[], { index, content, toolCalls, reasoning, reasoningSegments, metadata }: UpdateMessagePayload): any[] {
  const next = cloneMessages(messages);
  if (!next[index]) return next;
  if (content !== undefined) {
    next[index].content = content;
  }
  if (toolCalls !== undefined) {
    next[index].toolCalls = Array.isArray(toolCalls)
      ? toolCalls.map((item: any) => ({ ...item }))
      : toolCalls;
  }
  if (reasoning !== undefined) {
    next[index].reasoning = reasoning;
  }
  if (reasoningSegments !== undefined) {
    next[index].reasoningSegments = {
      ...(next[index].reasoningSegments || {}),
      ...reasoningSegments
    };
  }
  if (metadata !== undefined) {
    next[index].metadata = metadata && typeof metadata === 'object'
      ? {
          ...metadata,
          suggestions: Array.isArray(metadata.suggestions)
            ? metadata.suggestions.map((item: any) => ({ ...item }))
            : metadata.suggestions
        }
      : metadata;
  }
  return next;
}

function replaceMessageId(messages: any[], fromId: any, toId: any): any[] {
  if (!fromId || !toId) return cloneMessages(messages);
  return cloneMessages(messages).map((message: any) => {
    if (String(message._id) !== String(fromId)) return message;
    return {
      ...message,
      _id: toId
    };
  });
}

function buildStreamUpdate(messages: any[], data: any): UpdateMessagePayload | null {
  const lastMessageIndex = messages.length - 1;
  if (lastMessageIndex < 0 || messages[lastMessageIndex].role !== 'assistant') {
    return null;
  }

  const updateData: UpdateMessagePayload = { index: lastMessageIndex };
  if (data.streamPart === 'reasoning') {
    const reasoningIndex = data.reasoningIndex;
    if (reasoningIndex !== undefined && reasoningIndex !== null) {
      const reasoningSegments = {
        ...(messages[lastMessageIndex].reasoningSegments || {})
      };
      const currentSegment = reasoningSegments[reasoningIndex] || '';
      reasoningSegments[reasoningIndex] = currentSegment + (data.chunk || '');
      updateData.reasoningSegments = reasoningSegments;
    } else {
      const currentReasoning = messages[lastMessageIndex].reasoning || '';
      updateData.reasoning = currentReasoning + (data.chunk || '');
    }
  } else {
    const currentContent = messages[lastMessageIndex].content || '';
    updateData.content = currentContent + (data.chunk || '');
  }

  if (data.complete) {
    if (data.reasoning !== undefined) {
      updateData.reasoning = data.reasoning;
    }
    if (data.toolCalls && Array.isArray(data.toolCalls)) {
      updateData.toolCalls = data.toolCalls;
    }
    if (data.metadata !== undefined) {
      updateData.metadata = data.metadata;
    }
  }

  return updateData;
}

function getConversationTime(conv: any): number {
  if (!conv) return 0;
  const t = conv.modifiedTime || conv.createdTime;
  if (!t) return 0;
  const time = new Date(t).getTime();
  return Number.isNaN(time) ? 0 : time;
}

function sortConversations(conversations: any[]): any[] {
  if (!Array.isArray(conversations)) return [];
  return [...conversations].sort((a, b) => getConversationTime(b) - getConversationTime(a));
}

const agentModule: Module<AgentState, any> = {
  namespaced: true,
  state: {
    currentConversationId: null,
    messages: [],
    /** 流式中的会话消息临时缓存：切换走后仍保留已收到的分片，完成后删除 */
    streamMessageCache: {},
    conversations: [],
    isConnected: wsGateway.isConnected,
    connectionReadyState: wsGateway.getReadyState(),
    /** 正在接收流式回复的会话 id 集合（键为 conversationId 或 PENDING_NEW_STREAM_KEY） */
    streamingConvIds: {},
    ws: null,
    agentWsUnsubscribe: null,
    reconnectAttempts: 0,
    maxReconnectAttempts: 10,
    reconnectTimer: null,
    isManualDisconnect: false,
    /** 按会话隔离的消息待发送队列：{ [conversationKey]: [QueueItem] } */
    messageQueues: {},
    /** 尚未收到 chat HTTP 响应的 assistant 占位消息，支持停止后立即重试时延迟删除落库消息 */
    pendingAssistantMessages: {}
  },
  mutations: {
    SET_CURRENT_CONVERSATION_ID(state, conversationId) {
      state.currentConversationId = conversationId;
    },
    SET_MESSAGES(state, messages) {
      state.messages = messages;
    },
    ADD_MESSAGE(state, message) {
      state.messages.push(message);
    },
    UPDATE_MESSAGE(state, { index, content, toolCalls, reasoning, reasoningSegments, metadata }: UpdateMessagePayload) {
      if (state.messages[index]) {
        if (content !== undefined) {
          state.messages[index].content = content;
        }
        if (toolCalls !== undefined) {
          state.messages[index].toolCalls = toolCalls;
        }
        if (reasoning !== undefined) {
          Vue.set(state.messages[index] as any, 'reasoning', reasoning);
        }
        if (reasoningSegments !== undefined) {
          if (!(state.messages[index] as any).reasoningSegments) {
            Vue.set(state.messages[index] as any, 'reasoningSegments', {});
          }
          Object.assign((state.messages[index] as any).reasoningSegments, reasoningSegments);
        }
        if (metadata !== undefined) {
          Vue.set(state.messages[index] as any, 'metadata', metadata && typeof metadata === 'object'
            ? {
                ...metadata,
                suggestions: Array.isArray(metadata.suggestions)
                  ? metadata.suggestions.map((item: any) => ({ ...item }))
                  : metadata.suggestions
              }
            : metadata);
        }
        state.messages = [...state.messages];
      }
    },
    SET_CONVERSATIONS(state, conversations) {
      state.conversations = sortConversations(conversations);
    },
    ADD_CONVERSATION(state, conversation) {
      state.conversations = sortConversations([conversation, ...state.conversations]);
    },
    UPDATE_CONVERSATION(state, { id, updates, moveToTop = false }: { id: any; updates: any; moveToTop?: boolean }) {
      const idStr = id != null ? String(id) : '';
      const index = state.conversations.findIndex((c: any) => String(c._id) === idStr);
      if (index !== -1) {
        const next = { ...state.conversations[index], ...updates };
        const shouldMoveToTop = moveToTop || updates.modifiedTime !== undefined;
        if (shouldMoveToTop && index > 0) {
          const nextList = [...state.conversations];
          nextList.splice(index, 1);
          nextList.unshift(next);
          state.conversations = nextList;
        } else {
          Vue.set(state.conversations, index, next);
        }
      }
    },
    DELETE_CONVERSATION(state, conversationId) {
      state.conversations = state.conversations.filter((c: any) => c._id !== conversationId);
      if (state.currentConversationId === conversationId) {
        state.currentConversationId = null;
        state.messages = [];
      }
      if (conversationId && state.messageQueues[String(conversationId)]) {
        Vue.delete(state.messageQueues, String(conversationId));
        state.messageQueues = { ...state.messageQueues };
      }
      Object.keys(state.pendingAssistantMessages).forEach((tempId) => {
        const item = state.pendingAssistantMessages[tempId];
        if (item && item.conversationId === String(conversationId)) {
          Vue.delete(state.pendingAssistantMessages, tempId);
        }
      });
    },
    CLEAR_ALL_CONVERSATIONS(state) {
      state.conversations = [];
      state.currentConversationId = null;
      state.messages = [];
      state.streamingConvIds = {};
      state.streamMessageCache = {};
      state.messageQueues = {};
      state.pendingAssistantMessages = {};
    },
    DELETE_MESSAGE(state, messageId) {
      state.messages = state.messages.filter((m: any) => String(m._id) !== String(messageId));
    },
    REGISTER_PENDING_ASSISTANT(state, { tempId, conversationId }: { tempId: any; conversationId: any }) {
      if (!tempId) return;
      Vue.set(state.pendingAssistantMessages, String(tempId), {
        conversationId: conversationId != null ? String(conversationId) : null,
        deleteRequested: false
      });
    },
    MARK_PENDING_ASSISTANT_DELETE(state, tempId) {
      const key = String(tempId || '');
      if (!key || !state.pendingAssistantMessages[key]) return;
      Vue.set(state.pendingAssistantMessages, key, {
        ...state.pendingAssistantMessages[key],
        deleteRequested: true
      });
    },
    CLEAR_PENDING_ASSISTANT(state, tempId) {
      const key = String(tempId || '');
      if (key) Vue.delete(state.pendingAssistantMessages, key);
    },
    SET_PENDING_ASSISTANT_REAL_ID(state, { tempId, realId }: { tempId: any; realId: any }) {
      const key = String(tempId || '');
      if (!key || !state.pendingAssistantMessages[key]) return;
      Vue.set(state.pendingAssistantMessages, key, {
        ...state.pendingAssistantMessages[key],
        realId: realId != null ? String(realId) : null
      });
    },
    SET_CONNECTED(state, isConnected: boolean) {
      state.isConnected = isConnected;
      state.connectionReadyState = isConnected ? 1 : wsGateway.getReadyState();
    },
    SET_CONNECTION_READY_STATE(state, readyState: number) {
      state.connectionReadyState = readyState;
    },
    ADD_STREAMING_CONV(state, conversationKey: string) {
      if (!conversationKey) return;
      Vue.set(state.streamingConvIds, conversationKey, true);
    },
    REMOVE_STREAMING_CONV(state, conversationKey: string) {
      if (conversationKey && state.streamingConvIds[conversationKey]) {
        Vue.delete(state.streamingConvIds, conversationKey);
      }
    },
    REPLACE_STREAMING_CONV_KEY(state, { from, to }: { from: string; to: string }) {
      if (!from || !to || !state.streamingConvIds[from]) return;
      Vue.delete(state.streamingConvIds, from);
      Vue.set(state.streamingConvIds, to, true);
      if (state.streamMessageCache[from]) {
        Vue.set(state.streamMessageCache, String(to), cloneMessages(state.streamMessageCache[from]));
        Vue.delete(state.streamMessageCache, String(from));
      }
    },
    CLEAR_ALL_STREAMING_CONVS(state) {
      state.streamingConvIds = {};
    },
    ENQUEUE_MESSAGE(state, { conversationKey, item }: { conversationKey: string; item: AgentQueueItem }) {
      const key = String(conversationKey || PENDING_NEW_STREAM_KEY);
      const list = state.messageQueues[key] ? [...state.messageQueues[key]] : [];
      list.push(item);
      Vue.set(state.messageQueues, key, list);
      state.messageQueues = { ...state.messageQueues };
    },
    REMOVE_QUEUE_ITEM(state, { conversationKey, itemId }: { conversationKey: string; itemId: string }) {
      const key = String(conversationKey || PENDING_NEW_STREAM_KEY);
      if (!state.messageQueues[key]) return;
      const nextList = state.messageQueues[key].filter(x => x.id !== itemId);
      if (nextList.length > 0) {
        Vue.set(state.messageQueues, key, nextList);
      } else {
        Vue.delete(state.messageQueues, key);
      }
      state.messageQueues = { ...state.messageQueues };
    },
    UPDATE_QUEUE_ITEM(state, { conversationKey, itemId, updates }: { conversationKey: string; itemId: string; updates: any }) {
      const key = String(conversationKey || PENDING_NEW_STREAM_KEY);
      const list = state.messageQueues[key];
      if (!list) return;
      const index = list.findIndex(x => x.id === itemId);
      if (index !== -1) {
        const currentItem = list[index];
        const nextItem = {
          ...currentItem,
          ...updates,
          _isEditing: false,
          options: {
            ...(currentItem.options || {}),
            ...((updates && updates.options) || {})
          }
        };
        const nextList = [...list];
        nextList[index] = nextItem;
        Vue.set(state.messageQueues, key, nextList);
        state.messageQueues = { ...state.messageQueues };
      }
    },
    SET_QUEUE_ITEM_EDITING(state, { conversationKey, itemId, isEditing }: { conversationKey: string; itemId: string; isEditing: boolean }) {
      const key = String(conversationKey || PENDING_NEW_STREAM_KEY);
      const list = state.messageQueues[key];
      if (!list) return;
      const index = list.findIndex(x => x.id === itemId);
      if (index !== -1) {
        const nextItem = {
          ...list[index],
          _isEditing: Boolean(isEditing)
        };
        const nextList = [...list];
        nextList[index] = nextItem;
        Vue.set(state.messageQueues, key, nextList);
        state.messageQueues = { ...state.messageQueues };
      }
    },
    CLEAR_CONVERSATION_QUEUE(state, conversationKey: string) {
      const key = String(conversationKey || PENDING_NEW_STREAM_KEY);
      if (state.messageQueues[key]) {
        Vue.delete(state.messageQueues, key);
      }
      state.messageQueues = { ...state.messageQueues };
    },
    REPLACE_QUEUE_KEY(state, { from, to }: { from: string; to: string }) {
      if (!from || !to) return;
      const fromKey = String(from);
      const toKey = String(to);
      if (state.messageQueues[fromKey]) {
        const fromItems = state.messageQueues[fromKey].map(item => ({
          ...item,
          conversationKey: toKey
        }));
        const existing = state.messageQueues[toKey] || [];
        Vue.set(state.messageQueues, toKey, [...existing, ...fromItems]);
        Vue.delete(state.messageQueues, fromKey);
        state.messageQueues = { ...state.messageQueues };
      }
    },
    SET_WS(state, ws) {
      state.ws = ws;
    },
    SET_AGENT_WS_UNSUBSCRIBE(state, fn: (() => void) | null) {
      state.agentWsUnsubscribe = fn;
    },
    SET_RECONNECT_ATTEMPTS(state, attempts: number) {
      state.reconnectAttempts = attempts;
    },
    SET_IS_MANUAL_DISCONNECT(state, isManual: boolean) {
      state.isManualDisconnect = isManual;
    },
    SET_RECONNECT_TIMER(state, timer: any) {
      state.reconnectTimer = timer;
    },
    SET_STREAM_CACHE(state, { conversationId, messages }: { conversationId: any; messages: any[] }) {
      if (!conversationId) return;
      Vue.set(state.streamMessageCache, String(conversationId), cloneMessages(messages));
    },
    REMOVE_STREAM_CACHE(state, conversationId: any) {
      if (!conversationId) return;
      Vue.delete(state.streamMessageCache, String(conversationId));
    },
    CLEAR_STREAM_CACHE(state) {
      state.streamMessageCache = {};
    }
  },
  getters: {
    /** 任意会话是否在流式生成（侧边栏角标等可用） */
    isTyping(state): boolean {
      return Object.keys(state.streamingConvIds || {}).length > 0;
    },
    /** 指定会话是否正在流式生成（null 表示当前为「新会话」空白页） */
    isConversationStreaming: (state) => (conversationId?: string | number | null): boolean => {
      if (conversationId) {
        return !!state.streamingConvIds[String(conversationId)];
      }
      return !!state.streamingConvIds[PENDING_NEW_STREAM_KEY];
    },
    /** 当前活动会话 ID（null 表示新会话） */
    currentConversationId(state): string | number | null {
      return state.currentConversationId;
    },
    /** 当前活动会话队列的存储 key */
    currentConversationKey(state): string {
      return getConversationKey(state.currentConversationId);
    },
    /** 获取指定会话 key 的待发送消息队列 */
    conversationQueue: (state) => (conversationKey: string): AgentQueueItem[] => {
      return (state.messageQueues && state.messageQueues[conversationKey]) || [];
    },
    /** 获取当前活动会话的待发送消息队列 */
    currentConversationQueue(state, getters): AgentQueueItem[] {
      return (state.messageQueues && state.messageQueues[getters.currentConversationKey]) || [];
    },
    connectionStatus(state) {
      if (state.isConnected || state.connectionReadyState === 1) {
        return { status: 'connected', text: '已连接', color: 'green' };
      }
      switch (state.connectionReadyState) {
        case 0:
          return { status: 'connecting', text: '连接中...', color: 'yellow' };
        case 2:
          return { status: 'closing', text: '断开中...', color: 'yellow' };
        case 3:
        default:
          return { status: 'disconnected', text: '未连接', color: 'red' };
      }
    }
  },
  actions: {
    /**
     * 加载对话列表
     */
    async loadConversations({ commit }) {
      try {
        const res = await AgentService.getConversations();
        if (res.success) {
          commit('SET_CONVERSATIONS', res.data || []);
        }
      } catch (error) {
        console.error('加载对话列表失败:', error);
      }
    },

    /**
     * 持久化当前会话偏好（模型、Max Tokens、思考、上下文条数等）
     */
    async updateConversationSession({ commit }, payload: Record<string, any>) {
      const conversationId = payload.conversationId;
      const body = { ...payload };
      delete body.conversationId;
      const res = await AgentService.updateConversationSession(conversationId, body);
      if (res.success) {
        const updates: Record<string, any> = {};
        if (body.maxHistoryMessages !== undefined) {
          const n = Number(body.maxHistoryMessages);
          updates.maxHistoryMessages =
            Number.isFinite(n) && n >= 1 ? Math.min(500, Math.floor(n)) : 10;
        }
        if (body.chatModel !== undefined) {
          const cm = String(body.chatModel || '').trim();
          if (cm) updates.chatModel = cm.substring(0, 120);
        }
        if (body.chatMaxTokens !== undefined) {
          const t = Number(body.chatMaxTokens);
          if (Number.isFinite(t) && t >= 1024) {
            updates.chatMaxTokens = Math.min(256 * 1024, Math.floor(t));
          }
        }
        if (body.chatEnableReasoning !== undefined) {
          updates.chatEnableReasoning = !!body.chatEnableReasoning;
        }
        if (Object.keys(updates).length) {
          const nowStr = dates.formatDate(new Date(), 'yyyy-MM-dd HH:mm:ss');
          commit('UPDATE_CONVERSATION', {
            id: conversationId,
            updates: {
              ...updates,
              modifiedTime: nowStr
            },
            moveToTop: true
          });
        }
      }
      return res;
    },

    /**
     * 加载对话消息
     */
    async loadMessages({ commit, state }, conversationId: string | number) {
      try {
        const conversationKey = conversationId != null ? String(conversationId) : '';
        const cachedMessages = conversationKey ? state.streamMessageCache[conversationKey] : null;
        if (
          conversationKey &&
          cachedMessages &&
          state.streamingConvIds[conversationKey] &&
          state.currentConversationId === conversationId
        ) {
          commit('SET_MESSAGES', cloneMessages(cachedMessages));
        }

        const res = await AgentService.getMessages(conversationId);
        if (res.success) {
          if (state.currentConversationId === conversationId) {
            const nextMessages =
              conversationKey &&
              state.streamingConvIds[conversationKey] &&
              state.streamMessageCache[conversationKey]
                ? cloneMessages(state.streamMessageCache[conversationKey])
                : (res.data || []);
            commit('SET_MESSAGES', nextMessages);
            commit('SET_CURRENT_CONVERSATION_ID', conversationId);
          }
        }
      } catch (error) {
        console.error('加载消息失败:', error);
      }
    },

    /**
     * 发送消息
     */
    async sendMessage({ commit, state }, { message, conversationId, options = {} }: { message: string; conversationId?: any; options?: any }) {
      const streamKey = conversationId || PENDING_NEW_STREAM_KEY;
      const isRegenerate = options.regenerate === true;
      let tempAssistantMessageId: any = null;
      try {
        commit('ADD_STREAMING_CONV', streamKey);
        const attachments = Array.isArray(options.attachments)
          ? options.attachments.map((a: any) => ({
              url: a.url,
              name: a.name,
              mimeType: a.mimeType,
              size: a.size
            }))
          : [];

        if (!isRegenerate) {
          const userMessage: any = {
            _id: 'temp_' + Date.now(),
            role: 'user',
            content: message || '',
            attachments,
            createdAt: new Date()
          };
          commit('ADD_MESSAGE', userMessage);
        }

        if (conversationId) {
          const nowStr = dates.formatDate(new Date(), 'yyyy-MM-dd HH:mm:ss');
          commit('UPDATE_CONVERSATION', {
            id: conversationId,
            updates: {
              modifiedTime: nowStr
            },
            moveToTop: true
          });
        }

        const assistantMessage: any = {
          _id: 'temp_assistant_' + Date.now(),
          role: 'assistant',
          content: '',
          toolCalls: [],
          metadata: {
            suggestions: []
          },
          createdAt: new Date()
        };
        tempAssistantMessageId = assistantMessage._id;
        commit('ADD_MESSAGE', assistantMessage);
        commit('REGISTER_PENDING_ASSISTANT', {
          tempId: tempAssistantMessageId,
          conversationId
        });
        commit('SET_STREAM_CACHE', {
          conversationId: streamKey,
          messages: state.messages
        });

        const sendOpts = {
          ...options,
          attachments,
          streaming: true,
          regenerate: isRegenerate,
        };
        const optMh = Number(options.maxHistoryMessages);
        if (Number.isFinite(optMh) && optMh >= 1) {
          sendOpts.maxHistoryMessages = Math.min(500, Math.floor(optMh));
        }
        const res = await AgentService.sendMessage(message || '', conversationId, sendOpts);

        if (res.success) {
          const finalConversationId = res.data?.conversationId || conversationId;
          const realAssistantMessageId = res.data?.messageId;
          const pendingAssistant = state.pendingAssistantMessages[tempAssistantMessageId];

          if (realAssistantMessageId) {
            commit('SET_PENDING_ASSISTANT_REAL_ID', {
              tempId: tempAssistantMessageId,
              realId: realAssistantMessageId
            });

            if (pendingAssistant?.deleteRequested) {
              try {
                const deleteRes = await AgentService.deleteMessage(realAssistantMessageId);
                if (!deleteRes || deleteRes.success !== true) {
                  console.warn('删除待删除的旧 AI 消息失败:', deleteRes?.message || '未知错误');
                }
              } catch (deleteError) {
                console.error('删除待删除的旧 AI 消息失败:', deleteError);
              }
            }

            const nextMessages = replaceMessageId(
              state.messages,
              tempAssistantMessageId,
              realAssistantMessageId
            );
            commit('SET_MESSAGES', nextMessages);
            commit('SET_STREAM_CACHE', {
              conversationId: streamKey,
              messages: nextMessages
            });
          }
          commit('CLEAR_PENDING_ASSISTANT', tempAssistantMessageId);

          if (!conversationId && finalConversationId) {
            commit('REPLACE_STREAMING_CONV_KEY', {
              from: PENDING_NEW_STREAM_KEY,
              to: finalConversationId
            });
            commit('REPLACE_QUEUE_KEY', {
              from: PENDING_NEW_STREAM_KEY,
              to: finalConversationId
            });
          }

          if (finalConversationId) {
            commit('SET_STREAM_CACHE', {
              conversationId: finalConversationId,
              messages: state.messages
            });
          }

          if (!conversationId && finalConversationId) {
            commit('SET_CURRENT_CONVERSATION_ID', finalConversationId);
          }
          
          if (finalConversationId) {
            wsGateway.send('agent', {
              type: 'subscribe',
              payload: { conversationId: finalConversationId }
            });
          }
          
          if (!conversationId && finalConversationId) {
            (this as any).dispatch('agent/loadConversations').catch((error: any) => {
              console.error('加载对话列表失败:', error);
            });
          }
        } else {
          commit('CLEAR_PENDING_ASSISTANT', tempAssistantMessageId);
          commit('REMOVE_STREAMING_CONV', streamKey);
          commit('REMOVE_STREAM_CACHE', streamKey);
        }

        return res;
      } catch (error) {
        console.error('发送消息失败:', error);
        commit('CLEAR_PENDING_ASSISTANT', tempAssistantMessageId);
        commit('REMOVE_STREAMING_CONV', streamKey);
        commit('REMOVE_STREAM_CACHE', streamKey);
        throw error;
      }
    },

    /**
     * 删除对话
     */
    async deleteConversation({ commit }, conversationId: string | number) {
      try {
        const res = await AgentService.deleteConversation(conversationId);
        if (res.success) {
          commit('DELETE_CONVERSATION', conversationId);
          commit('REMOVE_STREAMING_CONV', String(conversationId));
          commit('REMOVE_STREAM_CACHE', String(conversationId));
        }
      } catch (error) {
        console.error('删除对话失败:', error);
        throw error;
      }
    },

    /** 清空当前用户全部会话（单次 API） */
    async clearAllConversations({ commit }) {
      const res = await AgentService.deleteAllConversations();
      if (res.success) {
        commit('CLEAR_ALL_CONVERSATIONS');
      }
      return res;
    },

    /**
     * 更新对话标题
     */
    async updateConversationTitle({ commit }, { conversationId, title }: { conversationId: string | number; title: string }) {
      try {
        const res = await AgentService.updateConversationTitle(conversationId, title);
        if (res.success) {
          const nowStr = dates.formatDate(new Date(), 'yyyy-MM-dd HH:mm:ss');
          commit('UPDATE_CONVERSATION', {
            id: conversationId,
            updates: {
              title,
              modifiedTime: nowStr
            },
            moveToTop: true
          });
        }
      } catch (error) {
        console.error('更新标题失败:', error);
        throw error;
      }
    },

    /**
     * 连接统一网关并监听 Agent 通道
     */
    connectWebSocket({ commit, state, dispatch }) {
      wsGateway.connect();

      if (!state.agentWsUnsubscribe) {
        const unsubMsg = wsGateway.on('agent', (data) => {
          dispatch('handleWebSocketMessage', data);
        });
        const unsubStatus = wsGateway.onStatus(({ connected, readyState }) => {
          commit('SET_CONNECTED', connected);
          if (typeof readyState === 'number') {
            commit('SET_CONNECTION_READY_STATE', readyState);
          }
          if (connected && state.currentConversationId) {
            dispatch('subscribeConversation', state.currentConversationId);
          }
        });

        commit('SET_AGENT_WS_UNSUBSCRIBE', () => {
          unsubMsg();
          unsubStatus();
        });
      }

      commit('SET_CONNECTED', wsGateway.isConnected);
      commit('SET_CONNECTION_READY_STATE', wsGateway.getReadyState());

      if (state.currentConversationId) {
        dispatch('subscribeConversation', state.currentConversationId);
      }
    },

    /**
     * 强制重新连接网关
     */
    reconnectWebSocket({ dispatch }) {
      wsGateway.reconnect();
      dispatch('connectWebSocket');
    },

    /**
     * 处理 WebSocket 消息
     */
    handleWebSocketMessage({ commit, state, dispatch }, data: any) {
      switch (data.type) {
        case 'stream': {
          const convId = data.conversationId;
          const conversationKey = convId != null ? String(convId) : '';
          const viewingThisConv =
            convId != null &&
            state.currentConversationId != null &&
            String(convId) === String(state.currentConversationId);
          const baseMessages = conversationKey && state.streamMessageCache[conversationKey]
            ? state.streamMessageCache[conversationKey]
            : (viewingThisConv ? state.messages : []);
          const updateData = buildStreamUpdate(baseMessages, data);

          if (conversationKey && updateData) {
            const cachedMessages = updateMessageList(baseMessages, updateData);
            commit('SET_STREAM_CACHE', {
              conversationId: conversationKey,
              messages: cachedMessages
            });
            if (viewingThisConv) {
              commit('SET_MESSAGES', cloneMessages(cachedMessages));
            }
          }

          if (data.complete && convId) {
            commit('REMOVE_STREAMING_CONV', conversationKey);
            commit('REMOVE_STREAM_CACHE', conversationKey);
            const nowStr = dates.formatDate(new Date(), 'yyyy-MM-dd HH:mm:ss');
            commit('UPDATE_CONVERSATION', {
              id: convId,
              updates: { modifiedTime: nowStr },
              moveToTop: true
            });
            dispatch('loadConversations').catch((error: any) => {
              console.error('刷新会话列表失败:', error);
            });
            const isViewingThisConv =
              state.currentConversationId != null &&
              String(state.currentConversationId) === String(convId);
            if (isViewingThisConv) {
              const doRefresh = () => {
                dispatch('loadMessages', convId).catch((error: any) => {
                  console.error('重新加载消息失败:', error);
                });
              };
              setTimeout(doRefresh, 200);
              setTimeout(doRefresh, 1200);
            }

            setTimeout(() => {
              dispatch('processNextInQueue', convId).catch((error: any) => {
                console.error('自动处理队列消息失败:', error);
              });
            }, 350);
          }
          break;
        }

        case 'tool_call': {
          const convId = data.conversationId;
          const conversationKey = convId != null ? String(convId) : '';
          const viewingThisConv =
            convId != null &&
            state.currentConversationId != null &&
            String(convId) === String(state.currentConversationId);
          const baseMessages = conversationKey && state.streamMessageCache[conversationKey]
            ? state.streamMessageCache[conversationKey]
            : (viewingThisConv ? state.messages : []);
          const toolCallMessageIndex = baseMessages.length - 1;
          if (toolCallMessageIndex >= 0 && baseMessages[toolCallMessageIndex].role === 'assistant') {
            const message = cloneMessage(baseMessages[toolCallMessageIndex]);
            const toolCalls = Array.isArray(message.toolCalls) ? [...message.toolCalls] : [];

            const existingIndex = toolCalls.findIndex((tc: any) => tc.id === data.toolCall.id);
            if (existingIndex >= 0) {
              toolCalls[existingIndex] = { ...toolCalls[existingIndex], ...data.toolCall };
            } else {
              toolCalls.push(data.toolCall);
            }

            const nextMessages = updateMessageList(baseMessages, {
              index: toolCallMessageIndex,
              toolCalls
            });
            if (conversationKey) {
              commit('SET_STREAM_CACHE', {
                conversationId: conversationKey,
                messages: nextMessages
              });
            }
            if (viewingThisConv) {
              commit('SET_MESSAGES', cloneMessages(nextMessages));
            }
          }
          break;
        }

        case 'message': {
          const conversationKey = data.conversationId != null ? String(data.conversationId) : '';
          const isViewingThisConv =
            data.conversationId != null &&
            state.currentConversationId != null &&
            String(data.conversationId) === String(state.currentConversationId);
          if (isViewingThisConv) {
            commit('ADD_MESSAGE', data.message);
          }
          if (conversationKey) {
            commit('REMOVE_STREAMING_CONV', conversationKey);
            commit('REMOVE_STREAM_CACHE', conversationKey);
            const nowStr = dates.formatDate(new Date(), 'yyyy-MM-dd HH:mm:ss');
            commit('UPDATE_CONVERSATION', {
              id: conversationKey,
              updates: { modifiedTime: nowStr },
              moveToTop: true
            });
            dispatch('loadConversations').catch(() => {});
            setTimeout(() => {
              dispatch('processNextInQueue', conversationKey).catch(() => {});
            }, 350);
          }
          break;
        }

        case 'stopped': {
          const convId = data.conversationId;
          const conversationKey = convId != null ? String(convId) : '';
          if (conversationKey) {
            commit('REMOVE_STREAMING_CONV', conversationKey);
            commit('REMOVE_STREAM_CACHE', conversationKey);
            const nowStr = dates.formatDate(new Date(), 'yyyy-MM-dd HH:mm:ss');
            commit('UPDATE_CONVERSATION', {
              id: convId,
              updates: { modifiedTime: nowStr },
              moveToTop: true
            });
          }
          const isViewingThisConv =
            state.currentConversationId != null &&
            String(state.currentConversationId) === String(convId);
          if (isViewingThisConv && convId) {
            const doRefresh = () => {
              dispatch('loadMessages', convId).catch(() => {});
            };
            setTimeout(doRefresh, 200);
            setTimeout(doRefresh, 1200);
          }
          break;
        }

        case 'error': {
          console.error('WebSocket 错误:', data.message);
          const errConvId = data.conversationId;
          const conversationKey = errConvId != null ? String(errConvId) : '';
          if (conversationKey) {
            commit('REMOVE_STREAMING_CONV', conversationKey);
            commit('REMOVE_STREAM_CACHE', conversationKey);
          } else {
            commit('CLEAR_ALL_STREAMING_CONVS');
            commit('CLEAR_STREAM_CACHE');
          }
          const isViewingErrConv =
            errConvId != null &&
            state.currentConversationId != null &&
            String(state.currentConversationId) === String(errConvId);
          if (isViewingErrConv && data.message) {
            const lastIdx = state.messages.length - 1;
            if (
              lastIdx >= 0 &&
              state.messages[lastIdx].role === 'assistant'
            ) {
              commit('UPDATE_MESSAGE', {
                index: lastIdx,
                content: `⚠️ ${data.message}`
              });
            } else {
              commit('ADD_MESSAGE', {
                _id: 'error_' + Date.now(),
                role: 'assistant',
                content: `⚠️ ${data.message}`,
                toolCalls: [],
                createdAt: new Date(),
                isError: true
              });
            }
          }
          break;
        }

        case 'conversation_title': {
          const cid = data.conversationId;
          const title = data.title;
          if (cid && title != null) {
            commit('UPDATE_CONVERSATION', {
              id: cid,
              updates: { title: String(title).trim() || '新对话' }
            });
          }
          break;
        }

        case 'assistant_metadata': {
          const convId = data.conversationId;
          const conversationKey = convId != null ? String(convId) : '';
          const viewingThisConv =
            convId != null &&
            state.currentConversationId != null &&
            String(convId) === String(state.currentConversationId);
          const baseMessages = conversationKey && state.streamMessageCache[conversationKey]
            ? state.streamMessageCache[conversationKey]
            : (viewingThisConv ? state.messages : []);
          const lastIndex = baseMessages.length - 1;
          if (lastIndex >= 0 && baseMessages[lastIndex].role === 'assistant') {
            const nextMessages = updateMessageList(baseMessages, {
              index: lastIndex,
              metadata: data.metadata
            });
            if (conversationKey) {
              commit('SET_STREAM_CACHE', {
                conversationId: conversationKey,
                messages: nextMessages
              });
            }
            if (viewingThisConv) {
              commit('SET_MESSAGES', cloneMessages(nextMessages));
            }
          }
          break;
        }

        case 'connected':
          break;

        default:
          console.warn('未知的 WebSocket 消息类型:', data.type);
      }
    },

    /**
     * 订阅对话
     */
    subscribeConversation(_, conversationId: string | number) {
      if (conversationId) {
        wsGateway.send('agent', {
          type: 'subscribe',
          payload: { conversationId }
        });
      }
    },

    /**
     * 取消订阅对话（仅发 WebSocket 消息，不修改 state）
     */
    unsubscribeConversation(_, conversationId: string | number) {
      if (conversationId) {
        wsGateway.send('agent', {
          type: 'unsubscribe',
          payload: { conversationId }
        });
      }
    },

    /**
     * 取消订阅当前对话并清空当前对话状态
     */
    clearCurrentConversation({ commit, state, dispatch }) {
      const conversationId = state.currentConversationId;
      if (conversationId) {
        dispatch('unsubscribeConversation', conversationId);
      }
      commit('SET_CURRENT_CONVERSATION_ID', null);
      commit('SET_MESSAGES', []);
    },

    /**
     * 离开 Agent 模块时注销监听与会话订阅
     */
    disconnectWebSocket({ commit, state, dispatch }) {
      if (state.currentConversationId) {
        dispatch('unsubscribeConversation', state.currentConversationId);
      }
      if (typeof state.agentWsUnsubscribe === 'function') {
        state.agentWsUnsubscribe();
        commit('SET_AGENT_WS_UNSUBSCRIBE', null);
      }
      commit('CLEAR_ALL_STREAMING_CONVS');
      commit('CLEAR_STREAM_CACHE');
      commit('SET_CONNECTED', false);
    },

    /**
     * 删除单条消息
     */
    async deleteMessage({ commit }, messageId: string | number) {
      try {
        const messageKey = messageId != null ? String(messageId) : '';
        if (!messageId || messageKey.startsWith('temp_') || messageKey.startsWith('error_')) {
          if (messageKey.startsWith('temp_assistant_')) {
            commit('MARK_PENDING_ASSISTANT_DELETE', messageKey);
          }
          commit('DELETE_MESSAGE', messageId);
          return { success: true };
        }
        const res = await AgentService.deleteMessage(messageId);
        if (res.success) {
          commit('DELETE_MESSAGE', messageId);
        }
        return res;
      } catch (error) {
        console.error('删除消息失败:', error);
        throw error;
      }
    },

    /**
     * 停止生成（通过 WebSocket 发送停止信号）
     */
    stopGeneration({ commit, state, dispatch }, payload: Record<string, any> = {}) {
      const conversationId =
        payload.conversationId != null
          ? payload.conversationId
          : state.currentConversationId;
      if (conversationId) {
        wsGateway.send('agent', {
          type: 'stop',
          payload: { conversationId }
        });
        commit('REMOVE_STREAMING_CONV', String(conversationId));
        commit('REMOVE_STREAM_CACHE', String(conversationId));
        const doRefresh = () => {
          dispatch('loadMessages', conversationId).catch((error: any) => {
            console.error('停止生成后重新加载消息失败:', error);
          });
        };
        setTimeout(doRefresh, 200);
        setTimeout(doRefresh, 1200);
      }
    },

    /**
     * 将消息推入会话待发送队列
     */
    enqueueMessage({ commit, getters }, { conversationKey, message, options = {} }: { conversationKey?: string; message: string; options?: any }) {
      const key = conversationKey || getters.currentConversationKey;
      const item: AgentQueueItem = {
        id: 'queue_' + Date.now() + '_' + Math.random().toString(36).slice(2, 8),
        conversationKey: key,
        message: (message && String(message).trim()) || '',
        options: {
          ...options,
          attachments: Array.isArray(options.attachments)
            ? options.attachments.map((a: any) => ({ ...a }))
            : []
        },
        createdAt: Date.now()
      };
      commit('ENQUEUE_MESSAGE', {
        conversationKey: key,
        item
      });
      return item;
    },

    /**
     * 移除队列中的某条消息
     */
    removeQueueItem({ commit }, { conversationKey, itemId }: { conversationKey: string; itemId: string }) {
      commit('REMOVE_QUEUE_ITEM', {
        conversationKey,
        itemId
      });
    },

    /**
     * 更新队列中的某条消息
     */
    updateQueueItem({ commit }, { conversationKey, itemId, updates }: { conversationKey: string; itemId: string; updates: any }) {
      commit('UPDATE_QUEUE_ITEM', {
        conversationKey,
        itemId,
        updates
      });
    },

    /**
     * 标记/取消标记队列某条消息处于用户编辑状态
     */
    setQueueItemEditing({ commit }, { conversationKey, itemId, isEditing }: { conversationKey: string; itemId: string; isEditing: boolean }) {
      commit('SET_QUEUE_ITEM_EDITING', {
        conversationKey,
        itemId,
        isEditing
      });
    },

    /**
     * 清空指定会话的待发送队列
     */
    clearConversationQueue({ commit }, conversationKey: string) {
      commit('CLEAR_CONVERSATION_QUEUE', conversationKey);
    },

    /**
     * 立即发送队列中的某条消息
     */
    async sendQueueItemImmediately({ commit, state, getters, dispatch, rootState }, { conversationKey, itemId }: { conversationKey: string; itemId: string }) {
      const user = rootState.user;
      if (!hasPermission(user, 'action.agent.messageQueue')) {
        throw new Error('没有消息队列操作权限');
      }

      const list = state.messageQueues[conversationKey] || [];
      const targetIndex = list.findIndex(x => x.id === itemId);
      if (targetIndex === -1) return;

      const targetItem = list[targetIndex];
      commit('REMOVE_QUEUE_ITEM', {
        conversationKey,
        itemId
      });

      const conversationId = conversationKey === PENDING_NEW_STREAM_KEY ? null : conversationKey;

      if (getters.isConversationStreaming(conversationId)) {
        dispatch('stopGeneration', { conversationId });
        await new Promise(resolve => setTimeout(resolve, 100));
      }

      return await dispatch('sendMessage', {
        message: targetItem.message,
        conversationId,
        options: targetItem.options
      });
    },

    /**
     * 处理指定会话队列中的下一条消息
     */
    async processNextInQueue({ commit, state, getters, dispatch, rootState }, conversationId: string | number) {
      const user = rootState.user;
      if (!hasPermission(user, 'action.agent.messageQueue')) {
        return;
      }

      const conversationKey = getConversationKey(conversationId);
      if (getters.isConversationStreaming(conversationId)) {
        return;
      }

      const list = state.messageQueues[conversationKey] || [];
      if (!list.length) {
        return;
      }

      const nextItem = list[0];
      if (nextItem && nextItem._isEditing) {
        return;
      }

      commit('REMOVE_QUEUE_ITEM', {
        conversationKey,
        itemId: nextItem.id
      });

      try {
        const targetConvId = conversationId || state.currentConversationId;
        return await dispatch('sendMessage', {
          message: nextItem.message,
          conversationId: targetConvId,
          options: nextItem.options
        });
      } catch (err) {
        console.error('[agent] processNextInQueue 发送失败:', err);
      }
    }
  }
};

export default agentModule;
